var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getUnderlyingPlayer } from "./useStreamDiagnostics";
/**
 * No sample yet, so every engine-derived reading is pending rather than
 * missing. Assumed present on purpose: reporting "not reported by this browser"
 * during the first second, before anything has been sampled, would be a guess
 * dressed up as an observation.
 */
var PENDING_ENGINE_CAPABILITIES = {
    known: false,
    path: "mse",
    delivery: true,
    renditions: true,
    frames: true
};
/**
 * True where the page can set the volume, as opposed to only muting.
 *
 * iOS leaves `volume` read-only, so the sound step must not ask for a slider
 * that the control bar does not even render there. Probed once on a detached
 * element, the same way video.js decides whether to show its volume control.
 */
var volumeControl;
export function canControlVolume() {
    if (volumeControl !== undefined)
        return volumeControl;
    var probe = document.createElement("video");
    probe.volume = 0.5;
    volumeControl = probe.volume === 0.5;
    return volumeControl;
}
/**
 * True where a showing text track exposes its cues.
 *
 * Native playback may render captions entirely inside the media stack, leaving
 * `cues` null on the track object the page holds. The cue poll then never sees
 * anything however well subtitles are working, so its silence has to be read
 * differently depending on this.
 */
export function canInspectCues(container) {
    var vjs = getUnderlyingPlayer(container);
    if (!vjs)
        return true;
    var tracks = vjs.textTracks();
    // No track to inspect yet is not a missing capability: the step that needs
    // cues is unavailable anyway when there is nothing to show.
    if (tracks.length === 0)
        return true;
    for (var i = 0; i < tracks.length; i++) {
        if (tracks[i].cues)
            return true;
    }
    return false;
}
/** Reads off one telemetry sample what this machine is able to report. */
export function detectCapabilities(diagnostics, container) {
    // Cue access and volume control are probed directly, so they are answered
    // from the first render: the sound step's wording depends on the second one,
    // and the tester may press Start before any sample has landed.
    var probed = { cues: canInspectCues(container), volume: canControlVolume() };
    if (!diagnostics)
        return __assign(__assign({}, PENDING_ENGINE_CAPABILITIES), probed);
    // The engine's counters are the one unambiguous marker of the playback path:
    // they exist wherever MSE is in play and nowhere else.
    var delivery = !!diagnostics.stats;
    return __assign({ known: true, path: delivery ? "mse" : "native", delivery: delivery, 
        // Both come from the same engine, so they stand or fall together. A
        // single-rendition MSE stream still *reports* its one rendition.
        renditions: delivery, frames: diagnostics.totalFrames !== undefined }, probed);
}
/** Human-readable playback path, for the panel and the copied report. */
export function describePath(capabilities) {
    if (!capabilities.known)
        return "not determined yet";
    return capabilities.path === "mse" ? "adaptive streaming (MSE)" : "native, handled by the browser";
}
/** The figures this machine could not produce, named so a report is read for what it omits. */
export function unobservable(capabilities) {
    var missing = [];
    // Only once something has been sampled can these be called absent rather
    // than pending.
    if (capabilities.known) {
        if (!capabilities.delivery)
            missing.push("throughput and segment errors");
        if (!capabilities.renditions)
            missing.push("renditions and adaptive switches");
        if (!capabilities.frames)
            missing.push("frame counters");
    }
    if (!capabilities.cues)
        missing.push("subtitle cues");
    if (!capabilities.volume)
        missing.push("volume level (mute only)");
    return missing;
}
/**
 * Identity for a track that survives a native track list.
 *
 * The engine gives every rendition an id; the native lists Safari builds from
 * an HLS manifest often leave `id` empty, and two empty ids compare equal, so
 * an id-only comparison reads a real language switch as no change at all and
 * fails the step. Language and label are what the tester actually picked, so
 * they belong in the key.
 */
export function trackKey(track) {
    return [track.id, track.language, track.label].filter(Boolean).join("|");
}
/**
 * Subtitle tracks a tester can really choose from the control bar.
 *
 * Safari synthesises a caption track for HLS streams that carry no subtitles at
 * all, with neither label nor language (the "Unknown" entry). The player keeps
 * it out of its own subs/caps menu until it produces a cue, so counting it here
 * would ask the tester to switch on a track that has no menu entry, and the
 * step could only ever time out as a failure.
 */
export function selectableTextTracks(player) {
    var _a;
    return ((_a = player === null || player === void 0 ? void 0 : player.getTextTracks()) !== null && _a !== void 0 ? _a : []).filter(function (track) { return !!(track.label || track.language); });
}
/** How many text tracks were dropped as unselectable, worth naming in a report. */
export function unnamedTextTrackCount(player) {
    var _a;
    return ((_a = player === null || player === void 0 ? void 0 : player.getTextTracks().length) !== null && _a !== void 0 ? _a : 0) - selectableTextTracks(player).length;
}
